import { IPaginationMetadata, ORDER_TYPE } from '@repo/ui';
import { useCallback, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';

interface UseDataTableRemotePaginationProps<EColumns> {
  initialColumnToOrderBy: EColumns;
  initialOrderType?: ORDER_TYPE;
}

export function useDataTableRemotePagination<TDataRow, EColumns>({
  initialColumnToOrderBy,
  initialOrderType,
}: UseDataTableRemotePaginationProps<EColumns>) {
  const [paginationMetadata, setPaginationMetadata] = useState<
    IPaginationMetadata<EColumns>
  >({
    selectedPage: 0,
    rowsPerPage: 10,
    searchString: '',
    orderColumn: initialColumnToOrderBy,
    sortDirection: initialOrderType || ORDER_TYPE.ASC,
  });

  // Methods
  const changePaginationMetadata = useCallback(
    <T extends keyof IPaginationMetadata<EColumns>>(
      key: T,
      value: IPaginationMetadata<EColumns>[T],
    ) => {
      setPaginationMetadata((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [],
  );

  const handleSortRemote = useCallback(
    (selectedColumn: TableColumn<TDataRow>, sortDirection: SortOrder) => {
      changePaginationMetadata(
        'orderColumn',
        selectedColumn.sortField as EColumns,
      );
      changePaginationMetadata(
        'sortDirection',
        sortDirection.toUpperCase() as ORDER_TYPE,
      );
      changePaginationMetadata('selectedPage', 0);
    },
    [],
  );

  const handlePageChange = useCallback((page: number) => {
    changePaginationMetadata('selectedPage', page - 1);
  }, []);

  const handleSearchRemote = useCallback((searchString: string) => {
    changePaginationMetadata('searchString', searchString);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    changePaginationMetadata('rowsPerPage', rowsPerPage);
  }, []);

  return {
    paginationMetadata,
    handleSortRemote,
    handlePageChange,
    handleSearchRemote,
    handleRowsPerPageChange,
  };
}
